import styled from "@emotion/styled";
import { Box, Tooltip } from "@mui/material";
import type { ExtractionListItemDto } from "Infrastructure/Api/Api";
import { BlDataTable } from "Shared/DataTable/BlDataTable";
import { useMemo } from "react";
import type { FC } from "react";

const StyledCell = styled(Box)``;

const StyledTextCell = styled(Box)`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const StyledUserCell = styled(Box)`
	display: flex;
	flex-direction: column;
`;

const StyledDateCell = styled(Box)`
	display: flex;
	flex-direction: column;
`;

const StyledSecondaryText = styled.div`
	font-size: 0.875em;
	color: #666;
`;

type ExtractionsTableProps = {
	extractions: ExtractionListItemDto[];
	searchQuery: string;
	isLoading?: boolean;
	totalCount?: number;
	onPageChange?: (page: number, rowsPerPage: number) => void;
};

export const ExtractionsTable: FC<ExtractionsTableProps> = ({
	extractions: rawExtractions,
	searchQuery,
	isLoading,
	totalCount,
	onPageChange,
}) => {
	// Filtering
	const filteredExtractions = useMemo(() => {
		return rawExtractions.filter((extraction) => {
			if (!searchQuery) return true;
			const query = searchQuery.toLowerCase();
			const userNameMatch = extraction.userName?.toLowerCase().includes(query);
			const userEmailMatch = extraction.userEmail
				?.toLowerCase()
				.includes(query);
			const configMatch = extraction.extractionConfigurationName
				?.toLowerCase()
				.includes(query);
			const tenantMatch = extraction.tenantCode?.toLowerCase().includes(query);
			return userNameMatch || userEmailMatch || configMatch || tenantMatch;
		});
	}, [rawExtractions, searchQuery]);

	return (
		<BlDataTable
			rows={filteredExtractions.map((x) => ({ data: x }))}
			rowEntityID="extractionID"
			isLoading={isLoading}
			totalCount={totalCount}
			onPageChange={onPageChange}
			getHeaderCell={(column) => <div>{column.label ?? column.id}</div>}
			columns={[
				{ id: "requestTime" as const, label: "Date & Time", maxWidth: 200 },
				{ id: "userName" as const, label: "User", fillRow: true },
				{
					id: "extractionConfigurationName" as const,
					label: "Configuration",
					fillRow: true,
				},
				{ id: "tenantCode" as const, label: "Tenant", maxWidth: 150 },
				{
					id: "inputTokens" as const,
					label: "Tokens (In/Out/Cached)",
					maxWidth: 200,
				},
			]}
			rowsPerPage={25}
			getCell={(id, data) => {
				switch (id) {
					case "requestTime": {
						const requestDate = new Date(data.requestTime);
						const dateStr = requestDate.toLocaleDateString();
						const timeStr = requestDate.toLocaleTimeString();
						return (
							<StyledDateCell>
								<div>{dateStr}</div>
								<StyledSecondaryText>{timeStr}</StyledSecondaryText>
							</StyledDateCell>
						);
					}
					case "userName":
						return (
							<Tooltip title={`${data.userName} (${data.userEmail})`} arrow>
								<StyledUserCell>
									<div>{data.userName}</div>
									<StyledSecondaryText>{data.userEmail}</StyledSecondaryText>
								</StyledUserCell>
							</Tooltip>
						);
					case "extractionConfigurationName":
						return (
							<Tooltip title={data.extractionConfigurationName} arrow>
								<StyledTextCell>
									{data.extractionConfigurationName}
								</StyledTextCell>
							</Tooltip>
						);
					case "tenantCode":
						return (
							<Tooltip title={data.tenantCode || "N/A"} arrow>
								<StyledTextCell>{data.tenantCode || "-"}</StyledTextCell>
							</Tooltip>
						);
					case "inputTokens":
						return (
							<StyledCell>
								{data.inputTokens} / {data.outputTokens} / {data.cachedTokens}
							</StyledCell>
						);
					default:
						return <StyledCell />;
				}
			}}
		/>
	);
};
